import { useState } from 'react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    interest: '',
    course: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d52upiqbfdatfusc424g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', organization: '', interest: '', course: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-32 bg-warmWhite">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex justify-center mb-8">
            <img 
              src="https://static.readdy.ai/image/ddb3f6bea97a6774f2df2489e0165868/38b77332d90057b7e2239d1e2b1e2298.png"
              alt="MasDeUna Logo"
              className="h-40 w-auto object-contain"
            />
          </div>
          <h2 className="font-poppins font-black text-5xl sm:text-6xl md:text-7xl text-charcoal mb-6 leading-tight">
            Let's Connect
          </h2>
          <div className="w-24 h-2 bg-primary mx-auto mb-8"></div>
          <p className="font-inter text-xl text-charcoal/80 font-medium">
            Ready to partner, collaborate, or learn more? Reach out.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-10 rounded-3xl shadow-xl" data-readdy-form>
          <div className="space-y-6">
            <div>
              <label htmlFor="name" className="block font-inter font-semibold text-charcoal mb-2">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm"
              />
            </div>

            <div>
              <label htmlFor="email" className="block font-inter font-semibold text-charcoal mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm"
              />
            </div>

            <div>
              <label htmlFor="organization" className="block font-inter font-semibold text-charcoal mb-2">
                Organization
              </label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm"
              />
            </div>

            <div>
              <label htmlFor="interest" className="block font-inter font-semibold text-charcoal mb-2">
                I'm interested in
              </label>
              <select
                id="interest"
                name="interest"
                value={formData.interest}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm cursor-pointer"
              >
                <option value="">Select an option</option>
                <option value="Becoming a Mentee">Becoming a Mentee</option>
                <option value="Becoming a Mentor">Becoming a Mentor</option>
                <option value="Partnership Opportunities">Partnership Opportunities</option>
                <option value="Workshop Information">Workshop Information</option>
                <option value="Running a Workshop">Running a Workshop</option>
                <option value="Donating/Supporting">Donating/Supporting</option>
                <option value="General Inquiry">General Inquiry</option>
              </select>
            </div>

            <div>
              <label htmlFor="course" className="block font-inter font-semibold text-charcoal mb-2">
                Which workshop are you interested in?
              </label>
              <select
                id="course"
                name="course"
                value={formData.course}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm cursor-pointer"
              >
                <option value="">Select a workshop</option>
                <option value="Leadership Fundamentals">Leadership Fundamentals</option>
                <option value="Executive Presence">Executive Presence</option>
                <option value="Negotiation Mastery">Negotiation Mastery</option>
                <option value="Strategic Networking">Strategic Networking</option>
                <option value="Financial Literacy">Financial Literacy</option>
                <option value="Giving and Receiving Feedback">Giving and Receiving Feedback</option>
                <option value="Strategic Delegation">Strategic Delegation</option>
                <option value="Personal Branding">Personal Branding</option>
                <option value="Time Management">Time Management</option>
                <option value="General Inquiry">General Inquiry</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block font-inter font-semibold text-charcoal mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm resize-none"
              ></textarea>
              <div className="text-right text-sm text-charcoal/60 mt-1 font-inter">
                {formData.message.length}/500
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary hover:bg-primary/90 text-white font-inter font-bold text-lg px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitStatus === 'success' && (
              <div className="bg-secondary/10 border border-secondary text-charcoal px-6 py-4 rounded-lg font-inter">
                Message sent successfully! We'll be in touch soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-primary/10 border border-primary text-charcoal px-6 py-4 rounded-lg font-inter">
                Something went wrong. Please try again.
              </div>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
